import { useState } from 'react'
import { useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import dayjs from 'dayjs'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import Button from '@mui/material/Button'
import KeyIcon from '@mui/icons-material/Key'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import Tooltip from '@mui/material/Tooltip'

import {
    UserRole,
    type User,
} from '@/types'
import { HISTORY_RETURN_PARAM_KEY, ROUTE_SEGMENTS, USER_ROLE_LABEL_MAP } from '@/constants/general'
import UserEmailForm from '@/components/forms/user-email'
import UserRoleForm from '@/components/forms/user-role'
import UserPasswordForm from '../forms/user-password'
import ToggleUserActive from './toggle-user-active'
import ApproveUser from './approve-user'
import SendUserWelcomeEmail from './send-user-welcome-email'
import PermissionsGate from '@/components/permissions-gate'
import { selectedCollectionPointIdAtom } from '@/state'
import { getUserMunicipalityId, getUserRole } from '@/helpers'

const userMunicipalityId = getUserMunicipalityId()
const userRole = getUserRole()

function UserDetail({
    user,
    handleClose,
}: {
    user: User,
    handleClose?: () => void,
}) {

    const navigate = useNavigate()
    
    const setCollectionPointId = useSetAtom(selectedCollectionPointIdAtom)

    const [editEmail, setEditEmail] = useState(false)
    const [editRole, setEditRole] = useState(false)
    const [changePassword, setChangePassword] = useState(false)

    const tieredRoles = [
        UserRole.admin,
        ...(user.role !== UserRole.admin ?
            [UserRole.eokoDataAdmin, UserRole.collectionCompany ] :
            []
    ),
        ...(user.role === UserRole.collectionPointAdmin || user.role === UserRole.resident ?
                [UserRole.municipalityOfficial] :
                []
        ),
    ]

    const handleClickDetail = (id: number) => {
        setCollectionPointId(id)
        navigate(`/${ROUTE_SEGMENTS.base}/${ROUTE_SEGMENTS.collectionPoint}/${ROUTE_SEGMENTS.detail}?${HISTORY_RETURN_PARAM_KEY}=true`)
    }

    const households = user.eoko_contacts_users_junctions.flatMap(junction =>
        junction.eoko_contact?.households?.flatMap(household =>
            household ? [household] : []
        ) ?? []
    )

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            position: 'relative',
            px: 3,
            pt: 1.5,
            pb: 2.5,
            flexGrow: 1,
        }}>
            <Box>
                <Typography variant="h3" component="h3">
                    Detail uživatele
                    <Box component="span" color="customGrey.secondaryText"> ID {user.id}</Box>
                </Typography>
            </Box>
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
            }}>
                <Box sx={{
                    flexBasis: {
                        xs: '100%',
                        md: '49%',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                    <Box sx={theme => ({
                        borderTop: `1px dashed ${theme.palette.customGrey.cardBorder}`,
                        pt: 1,
                    })}>
                        <Typography variant="h5" component="h3">
                            Aktivní
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                            {user.active ?
                                <Box sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: 'success.light',
                                }}/>
                                :
                                <Box sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: 'error.light',
                                }}/>
                            }
                            <Typography variant="body1" component="p">
                                {user.active ? 'Ano' : 'Ne'}
                            </Typography>
                        </Box>
                        <PermissionsGate
                            userRoles={tieredRoles}
                        >
                            <ToggleUserActive user={user} />
                        </PermissionsGate>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'flex-start',
                    }}>
                        {user.role !== UserRole.collectionPointAdmin && (editEmail ?
                            <UserEmailForm user={user} closeCallback={() => setEditEmail(false)}/>
                            :
                            <PermissionsGate
                                userRoles={tieredRoles}
                            >
                                <Button
                                    startIcon={<EditRoundedIcon />}
                                    onClick={() => setEditEmail(true)}
                                    color='terciary'
                                    variant="outlined"
                                >
                                    Upravit email
                                </Button>
                            </PermissionsGate>
                        )}
                        {user.role !== UserRole.collectionPointAdmin && (editRole ?
                            <UserRoleForm user={user} closeCallback={() => setEditRole(false)}/>
                            :
                            <PermissionsGate
                                userRoles={tieredRoles}
                            >
                                <Button
                                    startIcon={<EditRoundedIcon />}
                                    onClick={() => setEditRole(true)}
                                    color='terciary'
                                    variant="outlined"
                                >
                                    Upravit roli a příslušnost
                                </Button>
                            </PermissionsGate>
                        )}
                        {(user.role === UserRole.collectionPointAdmin && (user.eoko_contacts_users_junctions.length === 0 || households.length === 0)) && (editRole ?
                            <UserRoleForm user={user} closeCallback={() => setEditRole(false)}/>
                            :
                            <PermissionsGate
                                userRoles={[UserRole.admin]}
                            >
                                <Button
                                    startIcon={<EditRoundedIcon />}
                                    onClick={() => setEditRole(true)}
                                    color='terciary'
                                    variant="outlined"
                                >
                                    Upravit roli
                                </Button>
                            </PermissionsGate>
                        )}
                    </Box>
                    <Box sx={theme => ({
                        borderTop: `1px dashed ${theme.palette.customGrey.cardBorder}`,
                        pt: 1,
                    })}>
                        <Typography variant="h5" component="h3">
                            Schváleno
                        </Typography>
                        <Typography variant="body1" component="div" color="customGrey.secondaryText">
                            {user.approved_at ? dayjs(user.approved_at).format('D. M. YYYY HH:mm') : 'Ne'}
                            {user.approved_by_id ? ' uživatelem ID '+user.approved_by_id : null}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="h5" component="h3">
                            Odeslán přístupový email
                        </Typography>
                        <Typography variant="body1" component="div" color="customGrey.secondaryText">
                            {user.welcome_email_sent_at ? dayjs(user.welcome_email_sent_at).format('D. M. YYYY HH:mm') : 'Ne'}
                        </Typography>
                    </Box>
                    <Box>
                        {changePassword ?
                            <UserPasswordForm user={user} closeCallback={() => setChangePassword(false)}/>
                            :
                            <PermissionsGate
                                userRoles={tieredRoles}
                            >
                                <Button
                                    startIcon={<KeyIcon />}
                                    onClick={() => setChangePassword(true)}
                                    color='terciary'
                                    variant="outlined"
                                >
                                    Změnit heslo
                                </Button>
                            </PermissionsGate>
                        }
                        {!user.approved_at &&
                            <PermissionsGate
                                userRoles={tieredRoles}
                            >
                                <ApproveUser user={user} />
                            </PermissionsGate>
                        }
                        {user.active && user.approved_at &&
                            <PermissionsGate
                                userRoles={tieredRoles}
                            >
                                <SendUserWelcomeEmail user={user} />
                            </PermissionsGate>
                        }
                    </Box>
                </Box>
                <Box sx={{
                    flexBasis: {
                        xs: '100%',
                        md: '49%',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                    <Box sx={theme => ({
                        borderTop: `1px dashed ${theme.palette.customGrey.cardBorder}`,
                        pt: 1,
                    })}>
                        <Typography variant="h5" component="h3">
                            Email
                        </Typography>
                        <Typography variant="body1" component="p">
                            {user.email}
                        </Typography>
                    </Box>
                    <Box position="relative">
                        <Typography variant="h5" component="h3">
                            Role
                        </Typography>
                        <Typography variant="body1" component="p">
                            {USER_ROLE_LABEL_MAP[user.role]}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="h5" component="h3">
                            Svozová společnost
                        </Typography>
                        <Typography variant="body1" component="p">
                            {user.collection_company?.eoko_customer?.name ?
                                user.collection_company.eoko_customer.name
                            :
                            '-'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="h5" component="h3">
                            {user.role === UserRole.collectionPointAdmin ? 'Svozová místa' : 'Obec'}
                        </Typography>
                        {user.role === UserRole.collectionPointAdmin ?
                            <Box sx={{
                                display: 'flex',
                                gap: 1.5,
                                flexWrap: 'wrap',
                                mt: 1,
                            }}>
                                {households.map(household => (
                                    <Box key={household.id} sx={theme => ({
                                        p: 1.5,
                                        pt: 0.5,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2.5,
                                    })}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}>
                                            <Box sx={{ color: 'customGrey.secondaryText'}}>
                                                # {household.id}
                                            </Box>
                                            <Tooltip title="Detail svozového místa">
                                                <IconButton
                                                    onClick={() => handleClickDetail(household.id)}
                                                    size='small'
                                                    disabled={userRole === UserRole.municipalityOfficial && household.municipality?.id !== userMunicipalityId}
                                                >
                                                    <VisibilityRoundedIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Box>
                                            {household.municipality?.name}
                                        </Box>
                                        <Box sx={{ color: 'customGrey.secondaryText'}}>
                                            {`${household.address?.street} ${household.address?.street_number}`}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                            :
                            user.municipality ?
                                user.municipality.name
                            :
                            '-'
                        }
                    </Box>
                    <Box>
                        <Typography variant="h5" component="h3">
                            EOKO ID kontaktu
                        </Typography>
                        <Typography variant="body1" component="p">
                            {user.eoko_contacts_users_junctions.length > 0 ?
                                user.eoko_contacts_users_junctions.map(junction => junction.eoko_contact_id).join(', ')
                                : '-'
                            }
                        </Typography>
                    </Box>
                </Box>
            </Box>
            {handleClose &&
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '1rem',
                }}>
                    <IconButton
                        onClick={handleClose}
                        color="terciary"
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </Box>
            }
        </Box>
    )
}

export default UserDetail
